import { axios } from '@Api/axiosClient';
import type { IdvStatusResponse, IdvBeginCaptureResponse, StartCaptureFlowPayload, IdvCaptureOptions } from '@Types/MatchIdv.interface';

const baseUrl = 'api/Identity';

export const beginIdentityVerification = async (payload: StartCaptureFlowPayload): Promise<IdvBeginCaptureResponse> => {
    const response = await axios.post<IdvBeginCaptureResponse>(`${baseUrl}/StartCaptureFlow`, payload);
    return response.data;
};

/**
 * Which capture steps this flow needs. Falls back to requiring both: the Intellicheck path
 * cannot start without a document type, and a real capture cannot happen without a camera, so
 * guessing the other way would strand the recipient on a capture that cannot complete.
 */
export const getCaptureOptionsApi = async (companySlug: string): Promise<IdvCaptureOptions> => {
    try {
        const response = await axios.get<IdvCaptureOptions>(`${baseUrl}/GetCaptureOptions`, { params: { companySlug } });
        return response.data;
    } catch (error) {
        console.warn('[Request MFE] Failed to get capture options, keeping document selection:', error);
        return { documentSelectionRequired: true, deviceHandoffRequired: true };
    }
};

export const processPermissionsDeniedApi = async (): Promise<void> => {
    await axios.post(`${baseUrl}/ProcessPermissionsDenied`);
};

/**
 * Polls the IDV completion status for the current request.
 * Read-only: never triggers reconciliation or mutates server state.
 */
export const getIdvStatusApi = async (): Promise<IdvStatusResponse | undefined> => {
    try {
        const response = await axios.get<IdvStatusResponse>(`${baseUrl}/GetIdvStatus`);
        return response.data;
    } catch (error) {
        console.warn('[Request MFE] Failed to get IDV status:', error);
        return undefined;
    }
};

/**
 * Reconciles the IDV result for the current request: pulls results directly from
 * Intellicheck and runs the processing pipeline when the webhook never arrived
 * (e.g. cross-browser capture handoff — DS-4594). Safe to call repeatedly;
 * the idempotency guard on the BFF makes double-delivery a no-op.
 */
export const reconcileIdvStatusApi = async (): Promise<IdvStatusResponse | undefined> => {
    try {
        const response = await axios.post<IdvStatusResponse>(`${baseUrl}/ReconcileIdvStatus`);
        return response.data;
    } catch (error) {
        console.warn('[Request MFE] Failed to reconcile IDV status:', error);
        return undefined;
    }
};

export const createMobileSessionApi = async (): Promise<{ qrSessionToken: string } | undefined> => {
    try {
        const response = await axios.post<{ qrSessionToken: string }>(`${baseUrl}/CreateMobileSession`);
        return response.data;
    } catch (error) {
        console.warn('[Request MFE] CreateMobileSession failed:', error);
        return undefined;
    }
};
